import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import type { RootState } from 'store/store';

import IESLogo from 'components/Core/Header/GlobalHeader/IESLogo/IESLogo';
import QBOLogo from 'components/Core/Header/GlobalHeader/QBOLogo/QBOLogo';
import UserBar from 'components/Core/Header/UserBar/UserBar';
import { signIn, signOut } from 'store/auth/slice';
import { modalActions } from 'store/modal/slice';
import { toastActions } from 'store/toast/slice';
import { colors, breakpoints, paddingSize } from 'styles/cp';
import { Auth } from 'types/Auth';
import { CompanyInfo, Customizations } from 'types/CompanyInfo';
import { Config } from 'types/Config';
import { FeatureFlags } from 'types/FeatureFlags';
import { Modal } from 'types/Modal';

/** IES (Intuit Enterprise Suite) company branding: show IES logo when IXP/CDC flag cp-ies-company-branding is enabled. */
const isIESCompany = (featureFlags: FeatureFlags = {}): boolean =>
  !!featureFlags['cp-ies-company-branding'];

export type GlobalHeaderProps = {
  config: Pick<Config, 'dockerTag' | 'env' | 'endpoints'>;
  auth: Pick<Auth, 'isUserSignedIn' | 'username'>;
  ixp?: object;
  featureFlags?: FeatureFlags;
  hideUserBar?: boolean;
  showModal: (
    payload: Pick<Modal, 'component' | 'componentProps'>,
    state?: Partial<Modal> | undefined
  ) => void;
  showToast: ({ text }: { text: string }) => void;
  signOut: () => void;
  signIn: () => void;
  companyInfo: CompanyInfo;
  customizations: Customizations;
};

export const GlobalHeader: React.FC<GlobalHeaderProps> = (props) => {
  const {
    hideUserBar,
    config: { dockerTag, env },
    customizations,
    featureFlags = {},
  } = props;
  const shouldShowCPDockerTag = env && !env.includes('prod');
  const showIESLogo = isIESCompany(featureFlags);

  let logoContent: React.ReactNode;
  let logoAriaLabel: string;
  if (customizations?.logoUrl) {
    logoContent = <img src={customizations.logoUrl} height="50" alt="Company logo" />;
    logoAriaLabel = 'Company logo';
  } else if (showIESLogo) {
    logoContent = <IESLogo />;
    logoAriaLabel = 'IES Logo';
  } else {
    logoContent = <QBOLogo />;
    logoAriaLabel = 'QBO Logo';
  }

  return (
    <Fragment>
      <style jsx>{`
        .h {
          color: ${colors.black};
          height: 60px;
          margin: 0 auto;
          padding: ${paddingSize.xs} ${paddingSize.lg};
          justify-content: space-between;
          align-items: center;

          h1.logo {
            margin-bottom: 10px;
          }

          @media (max-width: ${breakpoints.md}) {
            padding: 15px 10px;
          }
        }
        .version {
          color: ${colors.gray02};

          @media (max-width: ${breakpoints.md}) {
            display: none;
          }
        }
      `}</style>

      <header className="w">
        <div className="h flex flex-any">
          <h1 className="logo" aria-label={logoAriaLabel}>
            {logoContent}
          </h1>
          {shouldShowCPDockerTag && dockerTag && (
            <span className="docker-tag">Docker: {dockerTag}</span>
          )}
          {!hideUserBar && (
            <UserBar
              config={props.config}
              showModal={props.showModal}
              showToast={props.showToast}
              auth={props.auth}
              signOut={props.signOut}
              signIn={props.signIn}
            />
          )}
        </div>
      </header>
    </Fragment>
  );
};
function mapStateToProps(state: RootState) {
  const { config, auth, ixp, companyInfo, featureFlags } = state;
  return {
    config,
    auth,
    ixp,
    featureFlags: featureFlags ?? {},
    customizations: companyInfo?.customizations,
  };
}
export default connect(mapStateToProps, {
  signOut,
  showModal: modalActions.show,
  showToast: toastActions.show,
  signIn,
})(GlobalHeader);
